/**
 * Where the account's record and the device key live (part 36a, principle C3): a database of their own,
 * `cockatrice-account`, beside the book's store and the backup log and never inside them, so no book record and no
 * store layout changes (C1; an existing tester's books stay put). It also says which account claimed the device's first
 * book store (deep-fix r4, table BK: each other account's books go in a store of their own). The device key is a WebCrypto key pair kept as the
 * browser clones it, its private half non-extractable; it outlives logouts, so a re-login on this device keeps the
 * device's ID and age (decision 55). Every change of the record is one read-and-put transaction, so two tabs changing
 * it never lose each other's change (CL14). Values are read at the boundary (`record.ts`, {@link readDeviceKey}).
 */
import { readAccountRecord } from './record.ts';
import type { AccountRecord } from './record.ts';
import type { DeviceKey } from './device.ts';
import { isAccountId } from './wire.ts';
import { RELEASED } from './shelf.ts';
import type { Claim } from './shelf.ts';
import type { AccountId } from './wire.ts';

/** The database's name and version (1: one store). */
export const ACCOUNT_DB = 'cockatrice-account';
const VERSION = 1;

/** The one store, and the keys of its three values. */
const STORE = 'account';
const RECORD_KEY = 'record';
const DEVICE_KEY = 'device';
/** Deep-fix r4, table BK: the account the device's first book store belongs to (the user's rule, 2026-10-24). */
const CLAIM_KEY = 'claim';
/** Part 36c, FD8, FD9: the book stores of deleted accounts, still to delete. */
const DOOMED_KEY = 'doomed';

/** FD8: the names of a deleted account's storage on the device: its book store, its backup log and its unsaved records' prefix. */
export interface DoomedNames {
  readonly books: string;
  readonly backups: string;
  readonly unsaved: string;
}

/** The queue as kept: each entry whose three names are text, the rest dropped. */
export function readDoomed(value: unknown): readonly DoomedNames[] {
  if (!Array.isArray(value)) return [];
  return value.flatMap((entry: unknown) => {
    if (typeof entry !== 'object' || entry === null) return [];
    const books: unknown = Reflect.get(entry, 'books');
    const backups: unknown = Reflect.get(entry, 'backups');
    const unsaved: unknown = Reflect.get(entry, 'unsaved');
    return typeof books === 'string' && typeof backups === 'string' && typeof unsaved === 'string' ? [{ books, backups, unsaved }] : [];
  });
}

/** The account's storage, as the controller uses it. */
export interface AccountStore {
  /** The record as kept (its defaults where it doesn't read). */
  record(): Promise<AccountRecord>;
  /** Changes the record in one transaction: `change` gets the record kept and gives the one to keep. Resolves to it. */
  change(change: (record: AccountRecord) => AccountRecord): Promise<AccountRecord>;
  /** The device key, or null for none (or one that doesn't read). */
  deviceKey(): Promise<DeviceKey | null>;
  /** Keeps `key` as the device key unless one is kept already; resolves to the one kept (CL14: two tabs, one key). */
  keepDeviceKey(key: DeviceKey): Promise<DeviceKey>;
  /** BK: the account the device's first book store belongs to, null while nobody has claimed it, or FC's release. */
  claim(): Promise<Claim>;
  /**
   * BK2: claims the first book store for `account` unless one claimed it already (or it was released, FC5); resolves to
   * the claim kept (CL14: two tabs, one claim).
   */
  claimFor(account: AccountId): Promise<AccountId | typeof RELEASED>;
  /**
   * FD8: `account`'s deletion is final: in one transaction, `names` queued for deletion, the first store's claim let go
   * when it is `account`'s, and the record changed by `change`. Resolves to the record kept.
   */
  doom(account: AccountId, names: DoomedNames, change: (record: AccountRecord) => AccountRecord): Promise<AccountRecord>;
  /** FD9: the stores queued for deletion. */
  doomed(): Promise<readonly DoomedNames[]>;
  /** FD9: the store named `books` deleted: taken off the queue. */
  cleared(books: string): Promise<void>;
  close(): void;
}

/** A write that didn't commit. */
export class AccountStoreError extends Error {
  override readonly name = 'AccountStoreError';

  constructor(cause: unknown) {
    super('a write to the account store was not kept', { cause });
  }
}

/** The device key as kept: a non-extractable ECDSA private key for signing and its SPKI; null for anything else. */
export function readDeviceKey(value: unknown): DeviceKey | null {
  if (typeof value !== 'object' || value === null) return null;
  const privateKey: unknown = Reflect.get(value, 'privateKey');
  const spki: unknown = Reflect.get(value, 'spki');
  if (!(privateKey instanceof CryptoKey) || !(spki instanceof Uint8Array) || spki.length === 0) return null;
  const curve: unknown = Reflect.get(privateKey.algorithm, 'namedCurve');
  if (privateKey.type !== 'private' || privateKey.extractable || !privateKey.usages.includes('sign') || curve !== 'P-256') return null;
  return { privateKey, spki: new Uint8Array(spki) };
}

function done<T>(request: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    request.onsuccess = () => {
      resolve(request.result);
    };
    request.onerror = () => {
      // eslint-disable-next-line @typescript-eslint/prefer-promise-reject-errors -- the browser's own error, passed through unchanged
      reject(request.error);
    };
  });
}

function committed(transaction: IDBTransaction): Promise<void> {
  return new Promise((resolve, reject) => {
    transaction.oncomplete = () => {
      resolve();
    };
    transaction.onabort = () => {
      reject(new AccountStoreError(transaction.error));
    };
  });
}

/** What opening the store takes besides the factory. */
export interface OpenAccountStoreOptions {
  /** The database name; {@link ACCOUNT_DB} unless a test isolates itself. */
  readonly name?: string;
  /** Called once the store has closed itself for a later build's upgrade. */
  readonly gone?: () => void;
}

/**
 * Opens the store on `factory` (`indexedDB`), creating it the first time. A later build's upgrade is never kept waiting:
 * this connection closes on `versionchange` and says so (`gone`). Version 1 is the first, so no older connection can
 * block its open; a later build's database refuses it (the open rejects).
 */
export async function openAccountStore(factory: IDBFactory, options: OpenAccountStoreOptions = {}): Promise<AccountStore> {
  const request = factory.open(options.name ?? ACCOUNT_DB, VERSION);
  request.onupgradeneeded = () => {
    request.result.createObjectStore(STORE);
  };
  const db = await done(request);
  db.onversionchange = () => {
    db.close();
    options.gone?.();
  };
  return indexedDbStore(db);
}

/** The store over an open database. */
function indexedDbStore(db: IDBDatabase): AccountStore {
  const read = async (key: string): Promise<unknown> => done(db.transaction(STORE, 'readonly').objectStore(STORE).get(key));
  /** A read-and-put of the value under `key`; `next` gives the value to put, or null to put none. A throw aborts it. */
  const change = async <T>(key: string, parse: (raw: unknown) => T, next: (now: T) => T | null): Promise<T> => {
    const transaction = db.transaction(STORE, 'readwrite');
    const objects = transaction.objectStore(STORE);
    let result = parse(undefined);
    const get = objects.get(key);
    get.onsuccess = () => {
      try {
        const now = parse(get.result);
        const put = next(now);
        if (put !== null) objects.put(put, key);
        result = put ?? now;
      } catch {
        transaction.abort();
      }
    };
    await committed(transaction);
    return result;
  };
  return {
    async record() {
      return readAccountRecord(await read(RECORD_KEY));
    },
    change(next) {
      return change(RECORD_KEY, readAccountRecord, next);
    },
    async deviceKey() {
      return readDeviceKey(await read(DEVICE_KEY));
    },
    async keepDeviceKey(key) {
      // None kept (or one that doesn't read) reads as `key` itself, which is then put; one kept is left as it is.
      return change<DeviceKey>(DEVICE_KEY, (raw) => readDeviceKey(raw) ?? key, (now) => (now === key ? key : null));
    },
    async claim() {
      const raw = await read(CLAIM_KEY);
      return isAccountId(raw) || raw === RELEASED ? raw : null;
    },
    async claimFor(account) {
      // Deep-fix r8: a claim kept is only read, never written again (a device that refuses writes still reads it); none
      // kept (or a value that doesn't read) is put, in the same transaction.
      const transaction = db.transaction(STORE, 'readwrite');
      const objects = transaction.objectStore(STORE);
      let claimer: AccountId | typeof RELEASED = account;
      const get = objects.get(CLAIM_KEY);
      get.onsuccess = () => {
        const kept: unknown = get.result;
        if (isAccountId(kept) || kept === RELEASED) claimer = kept;
        else objects.put(account, CLAIM_KEY);
      };
      await committed(transaction);
      return claimer;
    },
    async doom(account, names, next) {
      const transaction = db.transaction(STORE, 'readwrite');
      const objects = transaction.objectStore(STORE);
      let result = readAccountRecord(undefined);
      const record = objects.get(RECORD_KEY);
      record.onsuccess = () => {
        try {
          result = next(readAccountRecord(record.result));
          objects.put(result, RECORD_KEY);
        } catch {
          transaction.abort();
        }
      };
      const queue = objects.get(DOOMED_KEY);
      queue.onsuccess = () => {
        const kept = readDoomed(queue.result).filter((entry) => entry.books !== names.books);
        objects.put([...kept, names], DOOMED_KEY);
      };
      // FC1: the claimer's first store is released for good, never left for the next account to take (FC2).
      const claim = objects.get(CLAIM_KEY);
      claim.onsuccess = () => {
        if (claim.result === account) objects.put(RELEASED, CLAIM_KEY);
      };
      await committed(transaction);
      return result;
    },
    async doomed() {
      return readDoomed(await read(DOOMED_KEY));
    },
    async cleared(books) {
      await change(DOOMED_KEY, readDoomed, (now) => now.filter((entry) => entry.books !== books));
    },
    close() {
      db.close();
    },
  };
}
